/**
 * PHOTO IMPORT WIZARD
 * 
 * Handles bulk photo import with smart matching:
 * - Folder/ZIP upload with auto-matching
 * - URL import from CSV
 * - Manual matching for edge cases
 * - Compression before upload
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Upload, FolderOpen, Image, Check, X, AlertCircle,
  ChevronRight, ChevronLeft, Loader2, Search, RefreshCw,
  CheckCircle2, XCircle, HelpCircle, Trash2
} from 'lucide-react';
import { checkStorageBeforeUpload, estimateUploadSize } from '../services/photoUploadService';
import { addToStorageUsage } from '../services/storageService';
import { getCurrentUser } from '../firebase/auth';
import StorageUpgradeModal from './StorageUpgradeModal';
import { compressImage, ACCEPTED_IMAGE_TYPES } from '../utils/imageUtils';

// Types
interface PolishItem {
  id: string;
  brand: string;
  name: string;
  photos?: { url: string }[];
  externalPhotoUrl?: string;
}

interface PhotoFile {
  file: File;
  preview: string;
  folderPath: string;
  filename: string;
}

interface PhotoMatch {
  photo: PhotoFile;
  polishId: string | null;
  polishBrand: string | null;
  polishName: string | null;
  confidence: number; // 0-100
  matchReason: string;
  status: 'matched' | 'unmatched' | 'manual' | 'skipped';
}

interface PhotoImportWizardProps {
  polishes: PolishItem[];
  onImportComplete: (results: { polishId: string; photoUrl: string }[]) => void;
  onClose: () => void;
  uploadPhoto: (polishId: string, file: File) => Promise<string>; // Returns URL
}

// ============ UTILITY FUNCTIONS ============

/**
 * Normalize text for matching - removes special chars, lowercases
 */
function normalizeForMatching(text: string): string {
  return text
    .toLowerCase()
    .replace(/[''`]/g, '')           // Remove apostrophes
    .replace(/["\-–—_.,:;!?()[\]{}]/g, ' ')  // Replace punctuation with space
    .replace(/\s+/g, ' ')            // Multiple spaces to single
    .trim();
}

/**
 * Tokenize text into words for matching
 */
function tokenize(text: string): string[] {
  return normalizeForMatching(text)
    .split(' ')
    .filter(word => word.length > 1); // Skip single chars
}

/**
 * Calculate match score between two strings (0-100)
 * Uses token overlap with weighting for exact vs partial matches
 */
function calculateMatchScore(str1: string, str2: string): number {
  const tokens1 = tokenize(str1);
  const tokens2 = tokenize(str2);
  
  if (tokens1.length === 0 || tokens2.length === 0) return 0;
  
  let matchedTokens = 0;
  let partialMatches = 0;
  
  for (const t1 of tokens1) {
    // Exact match
    if (tokens2.includes(t1)) {
      matchedTokens++;
    } else {
      // Partial match (one contains the other)
      for (const t2 of tokens2) {
        if (t1.includes(t2) || t2.includes(t1)) {
          partialMatches += 0.5;
          break;
        }
      }
    }
  }
  
  const totalMatches = matchedTokens + partialMatches;
  const maxPossible = Math.max(tokens1.length, tokens2.length);
  
  return Math.round((totalMatches / maxPossible) * 100);
}

/**
 * Try to extract brand from folder path
 */
function extractBrandFromPath(folderPath: string): string | null {
  if (!folderPath) return null;
  
  // Get the immediate parent folder
  const parts = folderPath.split(/[/\\]/).filter(Boolean);
  if (parts.length === 0) return null;
  
  // Return the last folder in the path
  return parts[parts.length - 1];
}

/**
 * Clean filename for matching (remove extension, clean up)
 */
function cleanFilename(filename: string): string {
  // Remove extension
  const withoutExt = filename.replace(/\.[^.]+$/, '');
  // Replace common separators with spaces
  return withoutExt
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Find best matching polish for a photo
 */
function findBestMatch(
  photo: PhotoFile, 
  polishes: PolishItem[],
  threshold: number = 40
): { polish: PolishItem | null; confidence: number; reason: string } {
  const folderBrand = extractBrandFromPath(photo.folderPath);
  const cleanedFilename = cleanFilename(photo.filename);
  
  let bestMatch: PolishItem | null = null;
  let bestScore = 0;
  let matchReason = '';
  
  for (const polish of polishes) {
    let score = 0;
    let reasons: string[] = [];
    
    // Check brand match from folder
    if (folderBrand) {
      const brandScore = calculateMatchScore(folderBrand, polish.brand);
      if (brandScore >= 60) {
        score += 30; // Brand match bonus
        reasons.push(`Folder "${folderBrand}" → Brand`);
      }
    }
    
    // Check filename against polish name
    const nameScore = calculateMatchScore(cleanedFilename, polish.name);
    score += nameScore * 0.7; // Weight name match heavily
    
    if (nameScore >= 50) {
      reasons.push(`Filename → Name (${nameScore}%)`);
    }
    
    // Check filename against brand + name combo
    const comboScore = calculateMatchScore(cleanedFilename, `${polish.brand} ${polish.name}`);
    if (comboScore > nameScore) {
      score = Math.max(score, comboScore);
      reasons.push(`Filename → Brand+Name (${comboScore}%)`);
    }
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = polish;
      matchReason = reasons.join(', ');
    }
  }
  
  // Apply threshold
  if (bestScore < threshold) {
    return { polish: null, confidence: bestScore, reason: 'No confident match found' };
  }
  
  return { 
    polish: bestMatch, 
    confidence: Math.min(bestScore, 100), 
    reason: matchReason 
  };
}

// ============ MAIN COMPONENT ============

export default function PhotoImportWizard({ 
  polishes, 
  onImportComplete, 
  onClose,
  uploadPhoto 
}: PhotoImportWizardProps) {
  // State
  const [step, setStep] = useState<'select' | 'matching' | 'review' | 'uploading' | 'complete'>('select');
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [matches, setMatches] = useState<PhotoMatch[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<{ polishId: string; photoUrl: string }[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMatch, setSelectedMatch] = useState<number | null>(null);
  const [showStorageUpgradeModal, setShowStorageUpgradeModal] = useState(false);
  
  // Filter polishes without photos for matching
  const polishesNeedingPhotos = useMemo(() => {
    return polishes.filter(p => !p.photos?.length);
  }, [polishes]);
  
  // Stats
  const matchStats = useMemo(() => {
    const matched = matches.filter(m => m.status === 'matched' || m.status === 'manual').length;
    const unmatched = matches.filter(m => m.status === 'unmatched').length;
    const skipped = matches.filter(m => m.status === 'skipped').length;
    return { matched, unmatched, skipped, total: matches.length };
  }, [matches]);

  // ============ FILE HANDLING ============
  
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    
    const imageFiles: PhotoFile[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check if it's an image
      if (!file.type.startsWith('image/') && !file.name.match(/\.(jpg|jpeg|png|gif|webp|heic)$/i)) {
        continue;
      }
      
      // Get folder path from webkitRelativePath if available
      const relativePath = (file as any).webkitRelativePath || '';
      const folderPath = relativePath.split('/').slice(0, -1).join('/');
      
      // Create preview URL
      const preview = URL.createObjectURL(file);
      
      imageFiles.push({
        file,
        preview,
        folderPath,
        filename: file.name
      });
    }
    
    setPhotos(imageFiles);
    
    if (imageFiles.length > 0) {
      // Auto-match photos to polishes
      const newMatches: PhotoMatch[] = imageFiles.map(photo => {
        const { polish, confidence, reason } = findBestMatch(photo, polishesNeedingPhotos);
        
        return {
          photo,
          polishId: polish?.id || null,
          polishBrand: polish?.brand || null,
          polishName: polish?.name || null,
          confidence,
          matchReason: reason,
          status: polish && confidence >= 60 ? 'matched' : 'unmatched'
        };
      });
      
      setMatches(newMatches);
      setStep('matching');
    }
  }, [polishesNeedingPhotos]);

  // ============ MATCHING ACTIONS ============
  
  const handleManualMatch = useCallback((matchIndex: number, polishId: string) => {
    const polish = polishes.find(p => p.id === polishId);
    if (!polish) return;
    
    setMatches(prev => {
      const updated = [...prev];
      updated[matchIndex] = {
        ...updated[matchIndex],
        polishId: polish.id,
        polishBrand: polish.brand,
        polishName: polish.name,
        confidence: 100,
        matchReason: 'Manual match',
        status: 'manual'
      };
      return updated;
    });
    setSelectedMatch(null);
  }, [polishes]);
  
  const handleSkipMatch = useCallback((matchIndex: number) => {
    setMatches(prev => {
      const updated = [...prev];
      updated[matchIndex] = {
        ...updated[matchIndex],
        status: 'skipped'
      };
      return updated;
    });
  }, []);
  
  const handleRemoveMatch = useCallback((matchIndex: number) => {
    setMatches(prev => {
      const updated = [...prev];
      updated[matchIndex] = {
        ...updated[matchIndex],
        polishId: null,
        polishBrand: null,
        polishName: null,
        confidence: 0,
        matchReason: '',
        status: 'unmatched'
      };
      return updated;
    });
  }, []);
  
  const handleRerunMatching = useCallback(() => {
    const newMatches: PhotoMatch[] = photos.map(photo => {
      const { polish, confidence, reason } = findBestMatch(photo, polishesNeedingPhotos, 30);
      
      return {
        photo,
        polishId: polish?.id || null,
        polishBrand: polish?.brand || null,
        polishName: polish?.name || null,
        confidence,
        matchReason: reason,
        status: polish && confidence >= 60 ? 'matched' : 'unmatched'
      };
    });
    
    setMatches(newMatches);
  }, [photos, polishesNeedingPhotos]);

  // ============ UPLOAD ============
  
  const handleStartUpload = useCallback(async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setErrors(['Please log in to upload photos']);
      return;
    }
    
    const toUpload = matches.filter(m => m.polishId && (m.status === 'matched' || m.status === 'manual'));
    
    // Check storage before starting
    const filesToUpload = toUpload.map(m => m.photo.file);
    const estimatedSize = estimateUploadSize(filesToUpload);
    const storageCheck = await checkStorageBeforeUpload(currentUser.uid, estimatedSize);
    
    if (!storageCheck.canUpload) {
      setShowStorageUpgradeModal(true);
      return;
    }
    
    setStep('uploading');
    setUploadProgress(0);
    setErrors([]);
    
    const results: { polishId: string; photoUrl: string }[] = [];
    const uploadErrors: string[] = [];
    
    for (let i = 0; i < toUpload.length; i++) {
      const match = toUpload[i];
      
      try {
        // Compress image
        const compressed = await compressImage(match.photo.file);
        
        // Upload to Firebase
        const url = await uploadPhoto(match.polishId!, compressed);
        
        // Track storage usage
        await addToStorageUsage(currentUser.uid, compressed.size);
        
        results.push({ polishId: match.polishId!, photoUrl: url });
      } catch (err) {
        uploadErrors.push(`Failed to upload ${match.photo.filename}: ${err}`);
      }
      
      setUploadProgress(Math.round(((i + 1) / toUpload.length) * 100));
    }
    
    setUploadResults(results);
    setErrors(uploadErrors);
    setStep('complete');
  }, [matches, uploadPhoto]);

  // ============ FILTERED POLISHES FOR MANUAL MATCHING ============
  
  const filteredPolishes = useMemo(() => {
    if (!searchTerm.trim()) return polishesNeedingPhotos.slice(0, 50);
    
    const term = searchTerm.toLowerCase();
    return polishesNeedingPhotos
      .filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.brand.toLowerCase().includes(term)
      )
      .slice(0, 50);
  }, [polishesNeedingPhotos, searchTerm]);

  // ============ RENDER ============
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="glass-modal max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-600 bg-slate-800/60 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
              <Image className="icon-md text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-glass font-body">
                Photo Import Wizard
              </h2>
              <p className="text-sm text-glass-muted">
                {step === 'select' && 'Select photos to import'}
                {step === 'matching' && `${matchStats.matched} matched, ${matchStats.unmatched} need attention`}
                {step === 'review' && 'Review and confirm matches'}
                {step === 'uploading' && `Uploading... ${uploadProgress}%`}
                {step === 'complete' && `${uploadResults.length} photos imported`}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-800/60 rounded-full transition-default"
          >
            <X className="icon-md text-glass-muted" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Step 1: Select Files */}
          {step === 'select' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Folder Upload */}
                <label className="border-2 border-dashed border-slate-500 rounded-xl p-8 hover:border-slate-600 hover:bg-slate-700/50 transition-default cursor-pointer flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center">
                    <FolderOpen className="icon-lg text-glass-secondary" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-glass font-body">
                      Upload Folder
                    </p>
                    <p className="text-sm text-glass-muted mt-1">
                      Select a folder with photos organized by brand
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept={ACCEPTED_IMAGE_TYPES}
                    // @ts-ignore - webkitdirectory is valid but not in types
                    webkitdirectory=""
                    onChange={handleFileSelect}
                  />
                </label>
                
                {/* Individual Files */}
                <label className="border-2 border-dashed border-purple-500/50 rounded-xl p-8 hover:border-purple-400 hover:bg-purple-500/10 transition-default cursor-pointer flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Upload className="icon-lg text-purple-400" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-glass font-body">
                      Upload Files
                    </p>
                    <p className="text-sm text-glass-muted mt-1">
                      Select individual image files
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept={ACCEPTED_IMAGE_TYPES}
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
              
              {/* Tips */}
              <div className="alert-glass-purple">
                <h4 className="alert-glass-purple-title flex items-center gap-2 mb-2">
                  <HelpCircle className="icon-sm" />
                  Tips for Best Matching
                </h4>
                <ul className="alert-glass-purple-text space-y-1 list-disc list-inside">
                  <li>Organize photos in folders named after the brand</li>
                  <li>Name files with the polish name (e.g., "Bobs Burgers.jpg")</li>
                  <li>Supported formats: JPG, PNG, GIF, WebP, HEIC</li>
                  <li>Photos will be compressed automatically before upload</li>
                </ul>
              </div>
              
              {/* Stats */}
              <div className="bg-slate-800/60 rounded-xl p-4">
                <p className="text-sm text-glass-secondary">
                  <strong>{polishesNeedingPhotos.length}</strong> items in your collection need photos
                </p>
              </div>
            </div>
          )}
          
          {/* Step 2: Matching */}
          {step === 'matching' && (
            <div className="space-y-4">
              {/* Stats Bar */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 p-3 bg-slate-800/60 rounded-lg">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-status-success" />
                  <span className="text-xs sm:text-sm text-status-success"><strong>{matchStats.matched}</strong> matched</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <AlertCircle className="w-4 h-4 sm:icon-md text-status-warning" />
                  <span className="text-xs sm:text-sm text-status-warning"><strong>{matchStats.unmatched}</strong> need review</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <XCircle className="w-4 h-4 sm:icon-md text-glass-muted" />
                  <span className="text-xs sm:text-sm text-glass-muted"><strong>{matchStats.skipped}</strong> skipped</span>
                </div>
                <div className="flex-1" />
                <button
                  onClick={handleRerunMatching}
                  className="flex items-center gap-1 text-xs sm:text-sm text-glass-secondary hover:text-glass"
                >
                  <RefreshCw className="w-3.5 h-3.5 sm:icon-sm" />
                  <span className="hidden xs:inline">Re-run matching</span>
                  <span className="xs:hidden">Re-run</span>
                </button>
              </div>
              
              {/* Match List */}
              <div className="space-y-2 max-h-[60vh] sm:max-h-[50vh] overflow-auto">
                {matches.map((match, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border transition-default ${
                      match.status === 'matched' || match.status === 'manual'
                        ? 'bg-green-500/10 border-green-500/30'
                        : match.status === 'skipped'
                        ? 'bg-slate-800/60 border-slate-600 opacity-50'
                        : 'bg-amber-500/10 border-amber-500/30'
                    }`}
                  >
                    {/* Mobile: Stack layout, Desktop: Row layout */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      {/* Top row on mobile: Photo + filename + actions */}
                      <div className="flex items-start gap-3">
                        {/* Photo Preview */}
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                          <img 
                            src={match.photo.preview} 
                            alt={match.photo.filename}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Photo Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-glass truncate font-body text-sm sm:text-base">
                            {match.photo.filename}
                          </p>
                          {match.photo.folderPath && (
                            <p className="text-xs text-glass-muted truncate">
                              📁 {match.photo.folderPath}
                            </p>
                          )}
                        </div>

                        {/* Actions - visible on mobile in top row */}
                        <div className="flex items-center gap-1 sm:hidden flex-shrink-0">
                          {match.status !== 'skipped' && (
                            <>
                              <button
                                onClick={() => setSelectedMatch(selectedMatch === index ? null : index)}
                                className="p-1.5 text-glass-secondary hover:bg-slate-700 rounded-lg transition-default"
                                title="Manual match"
                              >
                                <Search className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleSkipMatch(index)}
                                className="p-1.5 text-glass-secondary hover:bg-slate-800/40 rounded-lg transition-default"
                                title="Skip"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Arrow - hidden on mobile */}
                      <ChevronRight className="icon-md text-glass-muted flex-shrink-0 hidden sm:block" />
                      
                      {/* Match Info */}
                      <div className="flex-1 min-w-0 pl-0 sm:pl-0">
                        {match.polishId ? (
                          <div className="bg-slate-800/40 sm:bg-transparent rounded-lg p-2 sm:p-0">
                            <p className="text-xs text-glass-muted sm:hidden mb-0.5">Matched to:</p>
                            <p className="font-medium text-glass truncate font-body text-sm sm:text-base">
                              {match.polishName}
                            </p>
                            <p className="text-xs text-glass-muted truncate">
                              {match.polishBrand}
                            </p>
                            <p className="text-xs text-green-400">
                              {match.confidence}% • {match.matchReason}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-amber-400 italic">
                            No match found
                          </p>
                        )}
                      </div>
                      
                      {/* Actions - desktop only */}
                      <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                        {match.status !== 'skipped' && (
                          <>
                            <button
                              onClick={() => setSelectedMatch(selectedMatch === index ? null : index)}
                              className="p-2 text-glass-secondary hover:bg-slate-700 rounded-lg transition-default"
                              title="Manual match"
                            >
                              <Search className="icon-sm" />
                            </button>
                            {match.polishId && (
                              <button
                                onClick={() => handleRemoveMatch(index)}
                                className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-default"
                                title="Remove match"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleSkipMatch(index)}
                              className="p-2 text-glass-secondary hover:bg-slate-800/40 rounded-lg transition-default"
                              title="Skip"
                            >
                              <X className="icon-sm" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Manual Match Dropdown */}
              {selectedMatch !== null && (
                <div className="glass-card-sm p-3 sm:p-4">
                  <h4 className="font-semibold text-glass mb-3 font-body text-sm sm:text-base">
                    Select Polish for: <span className="text-purple-400 break-all">{matches[selectedMatch].photo.filename}</span>
                  </h4>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-muted z-10" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by brand or name..."
                      className="input-glass pl-10 py-2.5 sm:py-2 font-body text-base sm:text-sm"
                    />
                  </div>
                  <div className="max-h-48 sm:max-h-48 overflow-auto space-y-1">
                    {filteredPolishes.map(polish => (
                      <button
                        key={polish.id}
                        onClick={() => handleManualMatch(selectedMatch, polish.id)}
                        className="w-full text-left p-2.5 sm:p-2 hover:bg-slate-700/60 rounded-lg transition-default active:bg-slate-600/60"
                      >
                        <p className="font-medium text-glass font-body">
                          {polish.name}
                        </p>
                        <p className="text-xs text-glass-muted">{polish.brand}</p>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setSelectedMatch(null)}
                    className="w-full mt-3 py-2 text-sm text-glass-secondary hover:text-glass font-body sm:hidden"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Step 3: Uploading */}
          {step === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-glass animate-spin mb-4" />
              <p className="text-lg font-semibold text-glass mb-2 font-body">
                Uploading Photos...
              </p>
              <div className="progress-bar-track w-64">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-glass-muted mt-2">{uploadProgress}% complete</p>
            </div>
          )}
          
          {/* Step 4: Complete */}
          {step === 'complete' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <Check className="icon-lg text-green-400" />
              </div>
              <p className="text-lg font-semibold text-glass mb-2 font-body">
                Import Complete!
              </p>
              <p className="text-glass-muted mb-4">
                Successfully uploaded {uploadResults.length} photos
              </p>
              
              {errors.length > 0 && (
                <div className="alert-glass-red max-w-md mb-4">
                  <p className="font-semibold text-red-300 mb-2">Some errors occurred:</p>
                  <ul className="text-sm text-red-200/80 space-y-1">
                    {errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {errors.length > 5 && (
                      <li>...and {errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
              
              <button
                onClick={() => {
                  onImportComplete(uploadResults);
                  onClose();
                }}
                className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-default font-body"
              >
                Done
              </button>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {(step === 'matching' || step === 'review') && (
          <div className="px-4 sm:px-6 py-4 border-t bg-slate-800/60 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <button
              onClick={() => setStep('select')}
              className="flex items-center justify-center sm:justify-start gap-2 px-4 py-2 text-glass-secondary hover:text-glass transition-default font-body order-2 sm:order-1"
            >
              <ChevronLeft className="icon-sm" />
              Back
            </button>
            
            <div className="text-sm text-glass-muted text-center sm:text-left order-1 sm:order-2">
              {matchStats.matched} photos ready to upload
            </div>
            
            <button
              onClick={handleStartUpload}
              disabled={matchStats.matched === 0}
              className="flex items-center justify-center gap-2 px-6 py-3 sm:py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-default font-body order-3"
            >
              Upload {matchStats.matched} Photos
              <ChevronRight className="icon-sm" />
            </button>
          </div>
        )}
      </div>

      {/* Storage Upgrade Modal */}
      {showStorageUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showStorageUpgradeModal}
          onClose={() => setShowStorageUpgradeModal(false)}
        />
      )}
    </div>
  );
}
